/**
 * Active-connector resolver — one source of truth for which provider the
 * admin frontend / public website should hand out.
 *
 * Reads `NEXT_PUBLIC_MAPS_PROVIDER` (website) or `VITE_MAPS_PROVIDER` (admin),
 * defaults to `'google'`. Resolved at module init — no React context, no
 * runtime switching. If per-customer choice is ever needed, swap this for a
 * hook without touching consumers (they import `getMapConnector()` etc.).
 *
 * Architecture: https://github.com/ogilviecreative/twm/issues/321#issuecomment-4536419887
 *
 * Phase 3 (#324): the Google geocode connector is now fully implemented
 * (forward/reverse + municipio resolution against `administrative_area_level_1`
 * with normalization to the canonical seed names). Mapbox geocode adapter
 * stays fully functional so `MAPS_PROVIDER=mapbox` is a working fallback.
 */

import type { MapConnector, GeocodeConnector } from './types';
import { mapboxMapsConnector, mapboxGeocodeConnector } from './providers/mapbox';
import { googleMapsConnector, googleGeocodeConnector } from './providers/google';

export type MapsProvider = 'google' | 'mapbox';

function resolveProvider(): MapsProvider {
  // Vite (admin frontend). Wrapped in try/catch because `import.meta` is a
  // parse error under some Node CJS bundles.
  try {
    // @ts-ignore — import.meta.env only exists under Vite
    const viteVal = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_MAPS_PROVIDER : undefined;
    if (viteVal === 'mapbox' || viteVal === 'google') return viteVal;
  } catch {
    // Fall through
  }
  // Next.js (public website).
  if (typeof process !== 'undefined' && process.env) {
    const nextVal = process.env.NEXT_PUBLIC_MAPS_PROVIDER;
    if (nextVal === 'mapbox' || nextVal === 'google') return nextVal;
  }
  return 'google';
}

const ACTIVE_PROVIDER: MapsProvider = resolveProvider();

const MAP_CONNECTORS: Record<MapsProvider, MapConnector> = {
  google: googleMapsConnector,
  mapbox: mapboxMapsConnector,
};

// Geocode resolver — provider-aligned with the MapConnector. LocationPickers
// + `shared/geocode.ts` callers import `getGeocodeConnector()` so flipping
// `MAPS_PROVIDER` toggles both the map view and the geocoding endpoint in
// lockstep.
const GEOCODE_CONNECTORS: Record<MapsProvider, GeocodeConnector> = {
  google: googleGeocodeConnector,
  mapbox: mapboxGeocodeConnector,
};

export function getActiveMapsProvider(): MapsProvider {
  return ACTIVE_PROVIDER;
}

export function getMapConnector(): MapConnector {
  return MAP_CONNECTORS[ACTIVE_PROVIDER];
}

export function getGeocodeConnector(): GeocodeConnector {
  return GEOCODE_CONNECTORS[ACTIVE_PROVIDER];
}

// Re-export the Phase 3 stubs so health checks / debug screens can probe them
// without spelunking through provider folders.
export { googleGeocodeConnector } from './providers/google';
export { mapboxGeocodeConnector } from './providers/mapbox';
export type * from './types';
